import React, { useEffect, useState, useRef } from 'react';
import { Bold, Italic, List, ListOrdered, Link, Quote, Code } from 'lucide-react';
interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  minHeight?: string;
}
export function RichTextEditor({
  value,
  onChange,
  placeholder,
  minHeight = '150px'
}: RichTextEditorProps) {
  const editorRef = useRef<HTMLDivElement>(null);
  const [isFocused, setIsFocused] = useState(false);
  // Simple execCommand wrapper for basic rich text
  const execCommand = (command: string, value: string | undefined = undefined) => {
    document.execCommand(command, false, value);
    if (editorRef.current) {
      editorRef.current.focus();
      onChange(editorRef.current.innerHTML);
    }
  };
  // Sync initial value
  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      // Only update if significantly different to avoid cursor jumping
      if (value === '' && editorRef.current.innerHTML === '<br>') return;
      if (editorRef.current.innerHTML === '' && value === '') return;
      // This is a simplified approach. For production, a real editor lib like TipTap is better.
      // But for this task, we'll initialize once if empty.
      if (editorRef.current.innerHTML === '') {
        editorRef.current.innerHTML = value;
      }
    }
  }, []);
  const handleInput = () => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };
  const ToolbarButton = ({
    icon: Icon,
    command,
    arg,
    label
  }: any) => <button type="button" onClick={() => execCommand(command, arg)} className="p-1.5 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded transition-colors" title={label} data-id="element-246">
      <Icon className="h-4 w-4" data-id="element-247" />
    </button>;
  return <div className={`relative border rounded-lg overflow-hidden bg-white transition-all duration-150 ${isFocused ? 'border-blue-500 ring-2 ring-blue-500/20' : 'border-slate-300 hover:border-slate-400'}`} data-id="element-248">
      <div className="flex items-center gap-0.5 px-2 py-1.5 border-b border-slate-200 bg-slate-50/80" data-id="element-249">
        <ToolbarButton icon={Bold} command="bold" label="Bold" data-id="element-250" />
        <ToolbarButton icon={Italic} command="italic" label="Italic" data-id="element-251" />
        <div className="w-px h-4 bg-slate-200 mx-1" data-id="element-252" />
        <ToolbarButton icon={List} command="insertUnorderedList" label="Bullet List" data-id="element-253" />
        <ToolbarButton icon={ListOrdered} command="insertOrderedList" label="Numbered List" data-id="element-254" />
        <div className="w-px h-4 bg-slate-200 mx-1" data-id="element-255" />
        <ToolbarButton icon={Quote} command="formatBlock" arg="blockquote" label="Quote" data-id="element-256" />
        <ToolbarButton icon={Code} command="formatBlock" arg="pre" label="Code Block" data-id="element-257" />
      </div>

      <div className="relative" data-id="element-258">
        <div ref={editorRef} contentEditable onInput={handleInput} onFocus={() => setIsFocused(true)} onBlur={() => setIsFocused(false)} className="p-3 focus:outline-none prose prose-sm max-w-none text-slate-800" style={{
        minHeight
      }} data-placeholder={placeholder} data-id="element-259" />

        {/* Placeholder simulation */}
        {(!value || value === '<br>') && !isFocused && <div className="absolute top-0 left-0 pointer-events-none text-slate-400 p-3 text-sm" onClick={() => editorRef.current?.focus()} data-id="element-260">
            {placeholder}
          </div>}
      </div>
    </div>;
}